/**
 * reportCell — how one report value is shown to a human, and written into an export.
 *
 * WHY THIS EXISTS
 * ---------------
 * The report table rendered every cell with one expression:
 *
 *     const display = v == null ? '—' : typeof v === 'object' ? JSON.stringify(v) : String(v);
 *
 * so the page looked like a database dump because it WAS one. Reported 2026-08-12 with two
 * screenshots. What a customer saw:
 *
 *   - `2026-08-07T00:56:56.844972+00:00` for a punch time — and the tenant is Asia/Shanghai, so
 *     the real arrival was 08:56. Eight hours wrong, in the artefact they hand to an accountant.
 *     (Fixed on the backend, which now sends org-local strings; this file keeps them readable.)
 *   - `early_leave` — a raw enum with an underscore.
 *   - `9.73` hours beside `2` minutes in the SAME row: two units to reconcile mentally.
 *   - `2026-08-07` with no weekday, on a report whose whole subject is weekday patterns.
 *
 * The export shared the defect and was worse for it: `handleExport` localized the column HEADERS
 * and passed the values through untouched, so the CSV that leaves the building carried the ISO
 * stamps and the raw enums.
 *
 * ONE formatter, used by the table and the export, keyed on the column NAME — because the backend
 * names its columns by meaning (`*_minutes` is a duration, `deduction_days` is a day count,
 * `status` is an enum) and that convention is the only thing both surfaces can agree on.
 */
import type { TFunctionLike } from './reportCellTypes';

/** Duration columns arrive as MINUTES. `payroll_export` is the deliberate exception below. */
const MINUTE_COLUMNS = /(^|_)(minutes)$/;

/** Decimal-hour columns, kept that way on purpose: payroll systems import decimal hours. */
const DECIMAL_HOUR_COLUMNS = /_hours$/;

/** Day counts — `deduction_days`, `deducted_days`, `absent_days`, `days_worked`… */
const DAY_COUNT_COLUMNS = /(^days_|_days$)/;

/** `YYYY-MM-DD` exactly. */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** `YYYY-MM-DD HH:MM` — what the backend now sends for a punch instant. */
const LOCAL_STAMP = /^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2})$/;

/** Columns whose value is an enum with a localized label. */
const ENUM_COLUMNS = new Set(['status', 'source', 'deduction_reason']);

/** Columns that are a plain yes/no. */
const BOOLEAN_COLUMNS = new Set(['missing_punch', 'within_geofence', 'authorized']);

export interface CellContext {
  /** i18n `t`, loose-typed for the same reason `translateApiError` is. */
  t: TFunctionLike;
  /** BCP-47 tag for date formatting, e.g. `zh-CN`. */
  locale: string;
}

/**
 * A duration in minutes as `h:mm`, or `Nm` under an hour.
 *
 * `h:mm` rather than decimal hours because that is what an HR reader recognises and what the
 * Excel templates SMBs already use look like; `7.6` invites being read as 7 h 60 min. The unit is
 * also stated in the column header, so the two never disagree.
 */
export function formatMinutes(total: number): string {
  const sign = total < 0 ? '-' : '';
  const abs = Math.abs(Math.round(total));
  if (abs === 0) return '0';
  if (abs < 60) return `${sign}${abs}m`;
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return `${sign}${h}:${String(m).padStart(2, '0')}`;
}

/** A date as the reader's locale writes it, WITH the weekday. */
export function formatReportDate(iso: string, locale: string): string {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  try {
    // Weekday included on purpose: an attendance reader scans for "late every Monday", and a bare
    // ISO date makes that invisible.
    return d.toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric', weekday: 'short' });
  } catch {
    return iso;
  }
}

/** `YYYY-MM-DD HH:MM` as `<localized date> HH:MM`. */
export function formatReportStamp(value: string, locale: string): string {
  const m = LOCAL_STAMP.exec(value);
  if (!m) return value;
  return `${formatReportDate(m[1], locale)} ${m[2]}`;
}

/**
 * The display string for one cell.
 *
 * Returns a STRING for both the table and the export, so the two cannot drift. A caller that wants
 * richer chrome (a status chip) can still branch on the column before calling this.
 */
export function formatReportCell(
  column: string,
  value: unknown,
  { t, locale }: CellContext,
): string {
  if (value === null || value === undefined || value === '') {
    // An empty punch time on a day that HAS a clock-in is a missed punch, and the row carries
    // `missing_punch` to say so — a dash alone reads as "nothing happened".
    return '—';
  }

  if (BOOLEAN_COLUMNS.has(column)) {
    return value
      ? t('attendance:attendanceReports.cell.yes')
      : t('attendance:attendanceReports.cell.no');
  }

  if (ENUM_COLUMNS.has(column)) {
    const raw = String(value);
    // `attendance:attendanceReports.values.<column>.<value>`, falling back to the raw token
    // de-underscored rather than showing `early_leave`.
    const label = t(`attendance:attendanceReports.values.${column}.${raw}`, { defaultValue: '' });
    return label || raw.replace(/_/g, ' ');
  }

  if (typeof value === 'number' || (typeof value === 'string' && /^-?\d+(\.\d+)?$/.test(value))) {
    const n = typeof value === 'number' ? value : Number(value);
    if (MINUTE_COLUMNS.test(column)) return formatMinutes(n);
    if (DECIMAL_HOUR_COLUMNS.test(column)) return n.toFixed(2);
    if (DAY_COUNT_COLUMNS.test(column)) {
      // 1 / 0.5 / 1.5 — never 1.00, and never a bare 0.5 without its unit in the header.
      return Number.isInteger(n) ? String(n) : n.toFixed(1);
    }
    return String(value);
  }

  if (typeof value === 'string') {
    if (LOCAL_STAMP.test(value)) return formatReportStamp(value, locale);
    if (ISO_DATE.test(value)) return formatReportDate(value, locale);
    return value;
  }

  if (typeof value === 'object') {
    // Was `JSON.stringify(v)` in a table cell. Nothing in a report should be an object; if one
    // appears, say so rather than pasting a brace.
    return t('attendance:attendanceReports.cell.unsupported');
  }
  return String(value);
}

/** Whether a column should be right-aligned — numbers and durations, not text. */
export function isNumericColumn(column: string): boolean {
  return (
    MINUTE_COLUMNS.test(column)
    || DECIMAL_HOUR_COLUMNS.test(column)
    || DAY_COUNT_COLUMNS.test(column)
  );
}
